import { ApiClientReturnType, Nullable } from '~/types';
import api from '../http-common';

export enum FixedPricePrefixType {
  FixedPrice = 'fixed-price',
  FatProtein = 'fat-protein',
  StockExchange = 'stock-exchange',
}

export const DashboardService = (prefix: FixedPricePrefixType) => {

  const getNextTradingSlot = () =>
    api.post(`/dashboard/${prefix}/get-next-trading-slot`);

  const getPreviousTradingSlot = () =>
    api.post(`/dashboard/${prefix}/get-previous-trading-slot`);

  const getChartTradingData = () =>
    api.post(`/dashboard/${prefix}/trading-activities/get-chart-data`);

  const getSettings = (): any =>
    api.post(`/${prefix}/trading-slots/get-settings-data`)

  return {
    getNextTradingSlot,
    getPreviousTradingSlot,
    getChartTradingData,
    getSettings
  }

}

export const getCo2Data = (data: { farmerId: string | number }): ApiClientReturnType<DashboardCO2Type> =>
  api.post('/dashboard/co2-footprint/get-data', data)

export const getCo2Options = (): ApiClientReturnType<DashboardCO2OptionsType> =>
  api.post('/dashboard/co2-footprint/get-options-data')

export const getClimaCheckData = (data: { farmerId: string | number }): ApiClientReturnType<DashboardClimaChType> =>
  api.post('/dashboard/climate-check-footprint/get-data', data)

export const getClimaCheckOptions = (): ApiClientReturnType<DashboardClimaOptionsType> =>
  api.post('/dashboard/climate-check-footprint/get-options-data')

export const getMilkMoneyData = (data: { farmerId: string | number; month?: string }): ApiClientReturnType<MilkMoneyDataType> =>
  api.post('/dashboard/milk-money/get-data', data)

export const getMilkMoneyOptions = (): ApiClientReturnType<MilkMoneyOptionsType> =>
  api.post('/dashboard/milk-money/get-options-data')

export const getCloudStorageDashboardData = (): ApiClientReturnType<FolderDashboardType> =>
  api.post('/dashboard/cloud-storage/get-data')

export const getSegmentationData = (data?: { farmerId?: string }): ApiClientReturnType<SegmentationDataType> =>
  api.post('/dashboard/segmentation/get-data', data)

export interface FolderDashboardType {
  folders: Folder[];
}

export interface Folder {
  id: string;
  name: string;
  visible: number;
  order: number;
  createdAt: Date;
  updatedAt: Date;
  visibleOnDashboard: number;
  code: string;
  enabled: number;
  icon: string;
  label: string;
  cloudStorageFolderId: string;
  languageId: number;
  title: string;
  files: File[];
}

export interface File {
  id: string;
  cloudStorageFolderId: string;
  views: number;
  visible: number;
  createdAt: string;
  updatedAt: Date;
  code: string;
  icon: string;
  title: string;
  type: string;
  link: null | string;
  filename: null | string;
  filetype: string;
  filesize: number | null;
  label: string;
  cloudStorageFileId: string;
  languageId: number;
}

export interface DashboardCO2Type {
  avgResultFarmer: Nullable<AvgResultFarmType>;
  defaultResult: number;
  lastCo2Footprint: Nullable<LastCo2Footprint>;
  co2Data: Nullable<Co2Data>;
  avgDairyResults: string | number;
}

export type AvgResultFarmType = {
  value: number | string,
  inventoryAddition: number | string,
  calfRearing: number | string,
  feeding: number | string,
  manureAndBedding: number | string,
  waterAndEnergy: number | string,
  dieselProductionAndCombustion: number | string,
  total: number | string
}

export interface Co2Data {
  id: string;
  totalMilkAmount: number;
  cowsNumber: string;
  milkAmount: number;
  breeds: Breed[];
  fat: number;
  protein: number;
}

export interface Breed {
  id: string;
  slug: string;
  status: number;
  label: string;
  co2FootprintAnswerId: number;
  breedId: number;
  percent: string;
}

export interface LastCo2Footprint {
  id: string;
  title: string;
  start: string;
  end: string;
  periodFrom: Date;
  periodTo: Date;
  uniqueId?: string;
  statusLabel: string;
  createdAt: Nullable<string>;
}

export interface DashboardCO2OptionsType {
  farmers: Farmer[];
  co2FootprintSettings: Co2FootprintSettings;
  texts: Text[];
}

export interface DashboardClimaOptionsType {
  farmers: Farmer[];
  climateCheckFootprintSettings: ClimateCheckFootprintSettings;
  texts: Text[];
}

export interface Co2FootprintSettings {
  bayernValueColor: string
  dairyValueColor: string
  yourValueColor: string
  showLflDefaultValue: number
}

export type ClimateCheckFootprintSettings =
  Omit<Co2FootprintSettings, 'bayernValueColor'> & {
    lflValueColor: string;
  }

export interface Farmer {
  farmerId: string;
  companyName: string;
  supplierNo: string;
}

export interface Text {
  slug: string;
  label: string;
  id: number;
  code: string;
  icon: string;
  translation: string;
  textId: number;
  languageId: number;
}

export interface DashboardClimaChType {
  avgResultFarmer: AvgResultFarmer;
  avgDairyResults: string;
  lastClimateCheckFootprint: LastClimateCheckFootprint;
  climateCheckData: ClimateCheckData;
  defaultResult: number;
}

export interface AvgResultFarmer {
  value: string;
  inventoryAddition: string;
  calfRearing: string;
  digestion: string;
  feedProductionAndLosses: string;
  manureAndBedding: string;
  waterAndEnergy: string;
  dieselProductionAndCombustion: string;
  machineManufacturing: string;
  total: string;
}

export interface ClimateCheckData {
  id: number;
  totalMilkAmount: number;
  cowsNumber: string;
  milkAmount: number;
  fat: string;
  protein: string;
  breed: number;
}

export interface LastClimateCheckFootprint {
  id: string;
  uniqueId: string;
  title: string;
  start: string;
  end: string;
  periodFrom: Date;
  periodTo: Date;
  status: number;
  completedAt: Date;
  statusLabel: string;
}

export interface MilkMoneyOptionsType {
  farmers: FarmerMilkMoney[];
  months: Month[];
}

export interface FarmerMilkMoney {
  id: string;
  companyName: string;
  evsNumber: string;
  managerId: string;
  farmerId: string;
}

export interface Month {
  key: string;
  label: string;
}

export interface MilkMoneyDataType {
  factors: Factor[];
  samples: Sample[];
  price: string;
  milkPayments: MilkPayment[];
  amounts: Amount[];
}

export interface Amount {
  date: Date;
  amount: string;
}

export interface Factor {
  tempMilkPaymentId: number;
  name: string;
  value: string;
  factorName?: string
}

export interface MilkPayment {
  id: number;
  farmerId: string;
  billingMonth: Date;
  evsNumber: string;
  agisId: string;
  farmerName: string;
  total: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Sample {
  tempMilkPaymentId: number;
  fat: string;
  protein: string;
  cellCount: number;
  date: string;
  germCount: number;
}

export interface SegmentationDataType {
  prices: Price[];
  files: SegmentationFileType[];
}

export interface SegmentationFileType {
  id: number;
  month: Date;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  code: string;
  order: number;
  enabled: number;
  icon: string;
  label: string;
  segmentationFileId: number;
  languageId: number;
  title: string;
  filename: string;
}

export interface Price {
  id: number;
  milkTypeId: number;
  month: Date;
  basePrice: string;
  aPrice: null;
  bPrice: null;
  planningBonus: string;
  ulDeduction: string;
  selectionBonus: string;
  aSegmentation: null;
  bSegmentation: null;
  priceSet: number;
  segmentationSet: number;
  createdAt: null;
  updatedAt: null;
  milkType: MilkType;
}

export interface MilkType {
  id: number;
  default: number;
  status: number;
  isBio: number;
  additionalInfo: null;
  languageId: number;
  label: string;
  milkTypeId: number;
  abbreviation: string;
  name: string;
}
